import { Box, Link, Stack, Theme, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusTextarea } from "@smartrr/shared/customerPortal/components/AquariusTextarea";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { CancellationActionEnum, ICancellationReason } from "@smartrr/shared/entities/CancellationRelationship";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { LocalizeKey, localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { shuffle } from "@smartrr/shared/utils/shuffle";
import isNil from 'lodash/isNil';
import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { toast } from "react-toastify";

//Components

import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import {
  applyRetentionDiscount,
  cancelSubscription,
} from "@smartrr/customer-portal/src/_state/actionCreators/customerPurchaseStates";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@smartrr/customer-portal/src/_state/typedCustomerReduxHooks";
import { useOrganization } from "@smartrr/customer-portal/src/account/views/OperationAquarius/utils";

import { CenterText } from "../CenterText";

// Helpers & Utils

import { AquariusBanner } from "../../../../../AquariusBanner";
import { ValidViews } from "../../OrderOptionsModalInner";
import { checkRetentionReasonUiIdx } from "../../../utils";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";
import { rgba } from "polished";

interface ICancelViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  setIconButtonInactive: () => void;
  setView: React.Dispatch<React.SetStateAction<ValidViews>>;
  theme: Theme;
  setCancellationReason: React.Dispatch<ICancellationReason>;
  saveUserCancellationData: (cancel?: boolean) => void;
  setUserCancellationReasonText: React.Dispatch<React.SetStateAction<string>>;
  userCancellationReasonText: string;
}

export function CancelView({
  delivery,
  setIconButtonInactive,
  setView,
  theme,
  setCancellationReason,
  saveUserCancellationData,
  setUserCancellationReasonText,
  userCancellationReasonText,
}: ICancelViewProps): JSX.Element {
  const localizeShorthand = "subscriptions.yourSubscriptions.manageSubscriptionModal";
  const { purchaseState } = delivery;
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const {
    cancellationReasons,
    isEnabled: hasRetention,
    isLoading: isRetentionLoading,
  } = useSmartrrCustomerSelector(state => state.retention);
  const { isLoading: isCPSLoading, isCancelLoading } = useSmartrrCustomerSelector(state => state.purchaseStates);
  const stackRef = useRef<HTMLDivElement>(null);
  const shuffledReasons = useMemo(() => {
    return shuffle(cancellationReasons);
  }, [cancellationReasons]);

  const [isDiscountLoading, setDiscountIsLoading] = useState<boolean>(false);
  const [selectedCancellationReason, setSelectedCancellationReason] = useState<undefined | ICancellationReason>(
    undefined
  );
  const [showCancelOptions, setShowCancelOptions] = useState<boolean>(false);
  const organization = useOrganization();

  const isPaused = useMemo(
    () => purchaseState.purchaseStateStatus === "PAUSED",
    [purchaseState.purchaseStateStatus]
  );
  const showPauseOptions = theme.modernThemeSettings?.subscriptionActions.pause && !isPaused;
  const isAvailable =
    !showCancelOptions &&
    hasRetention &&
    !!cancellationReasons?.length &&
    organization?.hasRetentionCancelReasonEnabled;
  const isPrepaid = isCPSPrepaid(purchaseState.schedule);

  const unSupportedPrepaidRetentionActions = [
    CancellationActionEnum.SWAP,
    CancellationActionEnum.FREQUENCY_UPDATE,
    CancellationActionEnum.DISCOUNT,
  ];

  const applyReasonDiscount = async () => {
    setDiscountIsLoading(true);
    if (selectedCancellationReason?.discount) {
      try {
        await dispatch(
          applyRetentionDiscount((-selectedCancellationReason.discount).toString(), purchaseState.id, toast)
        );
        setDiscountIsLoading(false);
        closeModal();
        saveUserCancellationData();
      } catch (error) {
        console.log(error);
        setDiscountIsLoading(false);
      }
    }
  };

  const handleReasonTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setUserCancellationReasonText(e.target.value);
    },
    [userCancellationReasonText, setUserCancellationReasonText]
  );

  const onCancelSubscription = useCallback(async (contractId: string) => {
    await dispatch(cancelSubscription(contractId));
    toast(localize(`${localizeShorthand}.cancelView.canceledToast`));
    closeModal();
  }, []);

  const formatButtonText = useCallback((): LocalizeKey => {
    switch (selectedCancellationReason?.action) {
      case CancellationActionEnum.GIFT: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.gift";
      }
      case CancellationActionEnum.SWAP: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.swap";
      }
      case CancellationActionEnum.FREQUENCY_UPDATE: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.editFrequency";
      }
      case CancellationActionEnum.CHANGE_DATE: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.changeDate";
      }
      case CancellationActionEnum.DISCOUNT: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.getDiscount";
      }
      case CancellationActionEnum.CHANGE_ADDRESS: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.editAddress";
      }
      case CancellationActionEnum.SKIP: {
        return "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.buttons.skipOrder";
      }
      default: {
        return "general.confirmChangesBtn";
      }
    }
  }, [selectedCancellationReason]);

  const formatModalContent = () => {
    switch (selectedCancellationReason?.action) {
      case CancellationActionEnum.GIFT: {
        return localize(`${localizeShorthand}.cancelView.giftModalMessage`);
      }
      case CancellationActionEnum.SWAP: {
        return localize(`${localizeShorthand}.cancelView.swapModalMessage`);
      }
      case CancellationActionEnum.FREQUENCY_UPDATE: {
        return localize(`${localizeShorthand}.cancelView.frequencyUpdateModalMessage`);
      }
      case CancellationActionEnum.CHANGE_DATE: {
        return localize(`${localizeShorthand}.cancelView.changeDateModalMessage`);
      }
      case CancellationActionEnum.DISCOUNT: {
        return localize(`${localizeShorthand}.cancelView.discountModalMessage`, {
          percentage: selectedCancellationReason?.discount,
        });
      }
      case CancellationActionEnum.CHANGE_ADDRESS: {
        return localize(`${localizeShorthand}.cancelView.changeAddressModalMessage`);
      }
      case CancellationActionEnum.SKIP: {
        return localize(`${localizeShorthand}.cancelView.skipModalMessage`);
      }
      default: {
        return "";
      }
    }
  };
  const formatModalClickEvent = useCallback(() => {
    switch (selectedCancellationReason?.action) {
      case CancellationActionEnum.GIFT: {
        return setView("Gift");
      }
      case CancellationActionEnum.SWAP: {
        return setView("Swap item");
      }
      case CancellationActionEnum.FREQUENCY_UPDATE: {
        return setView("Edit frequency");
      }
      case CancellationActionEnum.CHANGE_DATE: {
        return setView("Set next date");
      }
      case CancellationActionEnum.DISCOUNT: {
        return applyReasonDiscount();
      }
      case CancellationActionEnum.CHANGE_ADDRESS: {
        return setView("Edit address");
      }
      case CancellationActionEnum.SKIP: {
        return setView("Skip");
      }
      default: {
        return setView("Default");
      }
    }
  }, [selectedCancellationReason, setView]);

  const onCancelSubscriptionClick = useCallback(() => {
    if (isNil(purchaseState.shopifyId)) {
      throw new Error(`Couldn't get shopify id for subscription ${purchaseState.id}`);
    }
    if (
      selectedCancellationReason &&
      !showCancelOptions &&
      selectedCancellationReason?.action !== CancellationActionEnum.CANCEL
    ) {
      setShowCancelOptions(true);
      return;
    }

    saveUserCancellationToDB(selectedCancellationReason, false);
  }, [selectedCancellationReason, purchaseState.id, userCancellationReasonText]);

  const saveUserCancellationToDB = useCallback(
    (selectedReason: ICancellationReason | undefined, cancel: boolean) => {
      if (selectedReason && !cancel) {
        saveUserCancellationData();
      } else if (selectedReason && cancel) {
        saveUserCancellationData(cancel);
      }

      onCancelSubscription(purchaseState.id);
    },
    [
      purchaseState.id,
      purchaseState.shopifyId,
      selectedCancellationReason,
      userCancellationReasonText,
      saveUserCancellationData,
    ]
  );

  if (isCancelLoading || isDiscountLoading || isCPSLoading || isRetentionLoading) {
    return (
      <Stack height={stackRef.current?.clientHeight + "px"} justifyContent="center" alignItems="center">
        <DotSpinner />
      </Stack>
    );
  }

  return (
    <Stack ref={stackRef}>
      <Stack
        spacing="15px"
        justifyContent="center"
        alignItems="center"
        sx={{ padding: "30px 0px", marginTop: "15px" }}
        className={prefixClassName("cancel-subscription-wrapper")}
      >
        <React.Fragment>
          {isAvailable && organization.isRetentionCancelReasonRequired !== true ? (
            <Typography variant="caption1">{localize(`${localizeShorthand}.cancelView.optionalText`)}</Typography>
          ) : null}
          <CenterText
            sx={{ padding: "0px !important" }}
            title={
              showCancelOptions
                ? localize(`${localizeShorthand}.cancelView.cancelOptionsTitle`)
                : localize(
                    `${localizeShorthand}.cancelView.${isAvailable ? "retentionTitle" : "noRetentionTitle"}`
                  )
            }
          >
            {showCancelOptions ? null : (
              <Typography variant="body1" className={prefixClassName("cancel-warning-text")}>
                {localize(`${localizeShorthand}.cancelView.body`)}
                {!!showPauseOptions && (
                  <React.Fragment>
                    {" "}
                    <Trans
                      i18nKey={`${localizeShorthand}.cancelView.pauseText`}
                      components={[
                        <React.Fragment key={0} />,
                        <Link
                          className={prefixClassName("cancel-warning-text-pause")}
                          component="span"
                          key={1}
                          underline="always"
                          sx={{
                            color: theme.modernBodyFontColor,
                            cursor: "pointer !important",
                            textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                          }}
                          onClick={() => setView("Pause")}
                        />,
                      ]}
                    />
                  </React.Fragment>
                )}
              </Typography>
            )}
            {showCancelOptions ? formatModalContent() : null}
            {isPrepaid ? (
              <React.Fragment>
                <AquariusBanner state="warning">
                  <Typography variant="body1">
                    <Trans
                      i18nKey={`${localizeShorthand}.cancelView.prepaidNotice`}
                      components={[<React.Fragment key={0} />, <em key={1} />, <React.Fragment key={2} />]}
                    />
                  </Typography>
                </AquariusBanner>
              </React.Fragment>
            ) : (
              <React.Fragment />
            )}
          </CenterText>

          {isAvailable ? (
            <React.Fragment>
              {shuffledReasons
                ?.filter(cancellationReason => {
                  const validReason =
                    !cancellationReason.isArchived &&
                    !!cancellationReason?.text &&
                    Object.values(cancellationReason.text).every(text => !!text);

                  if (isPrepaid) {
                    return (
                      !unSupportedPrepaidRetentionActions.includes(
                        cancellationReason.action as CancellationActionEnum
                      ) && validReason
                    );
                  }
                  return validReason;
                })
                .map(cancellationReason => {
                  let updatedReason: ICancellationReason = cancellationReason;

                  if (
                    purchaseState.hasUsedRetentionDiscount &&
                    cancellationReason.action === CancellationActionEnum.DISCOUNT
                  ) {
                    updatedReason = {
                      ...cancellationReason,
                      action: CancellationActionEnum.CANCEL,
                    };
                  }

                  return (
                    <AquariusButton
                      sx={{ marginTop: "0px" }}
                      key={updatedReason.id}
                      onClick={() => {
                        setCancellationReason(updatedReason);
                        setSelectedCancellationReason(updatedReason);
                      }}
                      selected={selectedCancellationReason?.id === updatedReason.id}
                    >
                      {checkRetentionReasonUiIdx("cancelView.cancellationReason", updatedReason, shuffledReasons)}
                    </AquariusButton>
                  );
                })}
              {selectedCancellationReason?.hasCustomerPrompt ? (
                <Box sx={{ width: "100%" }}>
                  <AquariusTextarea
                    value={userCancellationReasonText}
                    placeholder={localize(`${localizeShorthand}.cancelView.textAreaPlaceholder`)}
                    maxLength={200}
                    onChange={handleReasonTextChange}
                  />
                </Box>
              ) : null}
            </React.Fragment>
          ) : null}
        </React.Fragment>
      </Stack>
      <ConfirmCancelButtons
        confirmTextOverride={selectedCancellationReason && showCancelOptions ? formatButtonText() : undefined}
        onCancel={setIconButtonInactive}
        onConfirm={
          showCancelOptions && selectedCancellationReason ? formatModalClickEvent : onCancelSubscriptionClick
        }
        disabled={
          !!isAvailable && !!organization.isRetentionCancelReasonRequired && isNil(selectedCancellationReason)
        }
      />
      {showCancelOptions ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Link
            onClick={() => {
              saveUserCancellationToDB(selectedCancellationReason, true);
            }}
            variant="caption1"
            underline="always"
            color={theme.modernCaptionFontColor}
            sx={{
              marginTop: 2,
              marginBottom: 2,
              "&:hover": {
                cursor: "pointer",
                color: theme.modernCaptionFontColor,
              },
            }}
          >
            {localize(`${localizeShorthand}.cancelView.continueCancelMessage`)}
          </Link>
        </Box>
      ) : null}
    </Stack>
  );
}
